import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { MenuItem, Stack, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import {
    setEcommerceTagAppliesTo,
    setEcommerceTagSolutionType,
    setSearchKeys,
} from "../../../lib/slices/searchKeysSlice";
import {
    deleteEcommerceTag,
    getAllEcommerceTags,
    updateEcommerceTag,
} from "../../../services/ecommerceTageApiFn";
import { handleOpenSnackBar } from "../../../utils";
import AddEditEcommerceTagDialog from "../../../components/Dialog/AddEditEcommerceTagsDialog";
import {
    ECOMMERCE_SOLUTION_TYPE,
    TAG_APPLIES_TO,
} from "../../../constants/Variables";
import CustomSelect from "../../../components/customSelect";
import { useTranslation } from "react-i18next";
import { deleteConfirmAlert } from "../../../components/Alert";

const headerData = [
    "createdAt",
    "name",
    "solutionType",
    "appliesTo",
    "order",
    "status",
    "featured",
    "actions",
];

const tableRows = [
    "created_date",
    "ecommerce_tag_name",
    "solution_type",
    "applies_to",
    "order",
    "status",
    "featured",
    "actions",
];

function EcommerceTags() {
    const dispatch = useDispatch();
    const openDialog = useRef(null);
    const [allTags, setAllTags] = useState([]);
    const { t } = useTranslation(["catalogue", "common"]);
    const [isLoading, setIsLoading] = useState(false);
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const debouncedSearchTerm = useDebounce(searchKeys.ecommerceTagName, 1000);
    const { currentLanguage } = useSelector((state) => state.languageReducer);

    const fetchAllEcommerceTags = async (name, solutionType) => {
        try {
            const params = {
                name: name,
                solutionType: solutionType,
                appliesTo: searchKeys.ecommerceTagAppliesTo,
            };
            setIsLoading(true);
            const response = await getAllEcommerceTags(params);
            if (response.data.status === "success") {
                let data = response?.data.data.reverse();
                setAllTags(data);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllEcommerceTags(
            debouncedSearchTerm,
            searchKeys.ecommerceTagSolutionType,
            searchKeys.ecommerceTagAppliesTo,
        );
    }, [
        debouncedSearchTerm,
        searchKeys.ecommerceTagSolutionType,
        searchKeys.ecommerceTagAppliesTo,
    ]);

    const handleEdit = (params) => {
        openDialog.current.openDialog({
            type: "edit",
            data: params.row,
            getData: fetchAllEcommerceTags,
        });
    };

    const handleChangeStatus = async (event, row, key) => {
        try {
            let payload = {
                [key]: event,
            };
            setIsLoading(true);
            const response = await updateEcommerceTag(row._id, payload);
            if (response.data.status === "success") {
                fetchAllEcommerceTags(
                    searchKeys.ecommerceTagName,
                    searchKeys.ecommerceTagSolutionType,
                    searchKeys.ecommerceTagAppliesTo,
                );
                handleOpenSnackBar("success", response.data.message);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTag = (params) => {
        deleteConfirmAlert({
            title: t("deleteConfirmation", { ns: "common" }),
            text: t("deleteConfirmationText", { ns: "common" }),
            confirmButtonText: t("delete", { ns: "common" }),
            cancelButtonText: t("cancel", { ns: "common" }),
        }).then(async (result) => {
            if (result) {
                try {
                    setIsLoading(true);
                    const response = await deleteEcommerceTag(params.row._id);
                    if (response.data.status === "success") {
                        fetchAllEcommerceTags(
                            searchKeys.ecommerceTagName,
                            searchKeys.ecommerceTagSolutionType,
                            searchKeys.ecommerceTagAppliesTo,
                        );
                        handleOpenSnackBar("success", response.data.message);
                    } else {
                        handleOpenSnackBar("error", response.data.message);
                    }
                } catch (error) {
                    handleOpenSnackBar("error", error.message);
                } finally {
                    setIsLoading(false);
                }
            }
        });
    }

    const prepareExportData = () => {
        return allTags.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY"),
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY"),
            "Icon": item.icon || "N/A",
            "Slug In Romanian": item.mlSlug?.ro || "N/A",
            "Slug In English": item.mlSlug?.en || "N/A",
            "Name In Romanian": item.name?.ro,
            "Name In English": item.name?.en,
            "Description In Romanian": item.description?.ro,
            "Description In English": item.description?.en,
            "Solution Type": item?.solutionType?.map(y => ECOMMERCE_SOLUTION_TYPE.find(item => item.value === y)?.label[currentLanguage]).join(", "),
            // "Applies To": item?.appliesTo?.map(y => TAG_APPLIES_TO.find(item => item.value === y)?.label[currentLanguage]).join(", "),
            Order: item.order,
            Status: item.status ? "Active" : "Inactive",
            Featured: item.featured ? "Yes" : "No",
        }));
    };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"contained"}
                    btnText={"addNew"}
                    handleClickBtn={() => {
                        openDialog.current.openDialog({
                            type: "add",
                            data: null,
                            getData: fetchAllEcommerceTags,
                        });
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />
                <CSVLink
                    data={prepareExportData()}
                    filename={"ecommerce_tags.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={"export"}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>
            </Stack>
        );
    };

    const renderSearchAction = () => {
        return (
            <Stack
                p={2}
                direction={{ md: "row" }}
                bgcolor={"primary.light"}
                gap={1}
                alignItems={"center"}
            >
                <TextInput
                    styleProps={{ width: { xs: "100%", md: "50%", xl: "50%" } }}
                    InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
                    InputEndIcon={
                        searchKeys.ecommerceTagName && (
                            <CancelOutlinedIcon
                                fontSize="small"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                    dispatch(
                                        setSearchKeys({
                                            ...searchKeys,
                                            ecommerceTagName: "",
                                        }),
                                    );
                                }}
                            />
                        )
                    }
                    placeholder={t("searchByName", { ns: "common" })}
                    value={searchKeys.ecommerceTagName}
                    onChange={(e) => {
                        dispatch(
                            setSearchKeys({
                                ...searchKeys,
                                ecommerceTagName: e.target.value,
                            }),
                        );
                    }}
                />
                <CustomSelect
                    placeholder={t("solutionType", { ns: "common" })}
                    formStyleProps={{
                        width: { xs: "100%", md: "25%" },
                        "& .MuiInputBase-input": {
                            textTransform: "capitalize",
                        },
                    }}
                    value={searchKeys.ecommerceTagSolutionType}
                    onChange={(e) => {
                        dispatch(setEcommerceTagSolutionType(e.target.value));
                    }}
                >
                    <MenuItem value="">{t("all", { ns: "common" })}</MenuItem>
                    {ECOMMERCE_SOLUTION_TYPE.map((val, ind) => {
                        return (
                            <MenuItem
                                key={ind}
                                value={val.value}
                                sx={{ textTransform: "capitalize" }}
                            >
                                {val.label[currentLanguage]}
                            </MenuItem>
                        );
                    })}
                </CustomSelect>
                <CustomSelect
                    placeholder={t("appliesTo", { ns: "common" })}
                    formStyleProps={{
                        width: { xs: "100%", md: "25%" },
                        "& .MuiInputBase-input": {
                            textTransform: "capitalize",
                        },
                    }}
                    value={searchKeys.ecommerceTagAppliesTo}
                    onChange={(e) => {
                        dispatch(setEcommerceTagAppliesTo(e.target.value));
                    }}
                >
                    <MenuItem value="">{t("all", { ns: "common" })}</MenuItem>
                    {TAG_APPLIES_TO.map((val, ind) => {
                        return (
                            <MenuItem
                                key={ind}
                                value={val.value}
                                sx={{ textTransform: "capitalize" }}
                            >
                                {val.label[currentLanguage]}
                            </MenuItem>
                        );
                    })}
                </CustomSelect>
            </Stack>
        );
    };

    const renderTable = useMemo(() => {
        return (
            <GlobalTable
                // tableWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allTags || []}
                showActions={true}
                showEdit={true}
                showDelete={true}
                handleEdit={handleEdit}
                handleDelete={handleDeleteTag}
                handleChangeStatus={(e, row, key) =>
                    handleChangeStatus(e, row, "status")
                }
                handleChangeFeatured={(e, row, key) =>
                    handleChangeStatus(e, row, "isFeatured")
                }
            />
        );
    }, [allTags]);

    return (
        <React.Fragment>
            <AppLoader isVisible={isLoading} />
            <PaperContainer
                title={t("ecommerceTagsList")}
                subtitle={t("ecommerceTagsDescription")}
                headerActions={renderHeaderAction()}
                searchActions={renderSearchAction()}
            >
                {renderTable}

                {allTags?.length === 0 && isLoading === false && (
                    <Typography variant="h5" fontWeight={600} textAlign={"center"}>
                        {t("noDataFound", { ns: "common" })}!
                    </Typography>
                )}
            </PaperContainer>

            <AddEditEcommerceTagDialog ref={openDialog} />
        </React.Fragment>
    );
}

export default EcommerceTags;
